/**
 * ThoughtProcessContent Component
 *
 * Main content area for displaying thought process text and reasoning steps
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  Box,
  Text,
  Stack,
  List,
  Tabs,
  Button,
  Collapse,
} from '@mantine/core';
import { IconFileText } from '@tabler/icons-react';
import classes from './ThoughtProcessContent.module.css';
import DOMPurify from 'dompurify';
import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { MessageMarkdown } from '../../MessageMarkdown';
import { useMessagesWithStore } from '../../../../../hooks/useMessages';
import { Reference } from '../../../types/conversation';
import { stripOrchestratorAgentSections } from '../../../utils/thoughtProcessUtils';
import {
  truncateSourceChunksInMarkdownForDisplay,
  truncateSourceTextForDisplay,
} from '../../../utils/sourceContentTruncation';

// =============================================================================
// COMPONENT INTERFACES
// =============================================================================

export interface ThoughtProcessContentProps {
  content: string;
  agent_classification?: string[];
  data_points?: Reference[];
  className?: string;
}


const RETRIEVED_DOCUMENTS_HEADING_REGEX = /^#{1,6}\s*Retrieved Documents\s*$/im;
const SOURCE_LINK_REGEX = /\[Source\]\([^)]+\)/i;

const splitRetrievedDocuments = (content: string) => {
  const headingMatch = RETRIEVED_DOCUMENTS_HEADING_REGEX.exec(content);
  if (!headingMatch) {
    return null;
  }

  const sourceContent = content.slice(headingMatch.index + headingMatch[0].length).trim();
  if (!sourceContent || !SOURCE_LINK_REGEX.test(sourceContent)) {
    return null;
  }

  return {
    leadContent: content.slice(0, headingMatch.index).trim(),
    sourceContent,
  };
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ThoughtProcessContent: React.FC<ThoughtProcessContentProps> = ({
  content,
  agent_classification,
  data_points,
  className,
}) => {
  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const { isLoading } = useMessagesWithStore({ conversationID: '', messageID: '' });
  const [sourceDocumentsExpanded, setSourceDocumentsExpanded] = useState(false);

  const renderDataPoints = useMemo(() => {
    if (!data_points || data_points.length === 0) {
      return <Text size="sm" c="dimmed" className={classes.sourceUnavailable}>No source paragraphs available</Text>;
    }

    return data_points.map((source, i) => {
      const displayContent = truncateSourceTextForDisplay(source.content);

      return (
        <div key={i} className={classes.sourceParagraph}>
          <Text className={classes.sourceTitle}>{source.title}</Text>
          <Text className={classes.sourceText}>{displayContent}</Text>
        </div>
      );
    });
  }, [data_points]);

  const renderMainContent = useCallback(() => {
    if (isLoading) {
      return (
        <Box className={className}>
          <Stack gap="md" p="md">
            <Text size="sm" c="dimmed">Loading thought process...</Text>
          </Stack>
        </Box>
      );
    }
    const strippedContent = stripOrchestratorAgentSections(content || '');
    const retrievedDocuments = splitRetrievedDocuments(strippedContent);
    const thoughtContent = retrievedDocuments ? retrievedDocuments.leadContent : strippedContent;
    const displayContent = truncateSourceChunksInMarkdownForDisplay(thoughtContent);
    const sanitizedThoughts = DOMPurify.sanitize(displayContent);

    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        // Remove <br> elements since we handle spacing with margins
        if (domNode.type === 'tag' && domNode.name === 'br') {
          return <></>;
        }
        // If it's a text node, render with MessageMarkdown
        if (domNode.type === 'text') {
          return (
            <MessageMarkdown
              content={domNode.data}
              onCitationClick={() => {}}
              onFollowUpClick={() => {}}
              onCitationsExtracted={() => {}}
              className={''}
            >
            </MessageMarkdown>
          );
        }
        // For other nodes, let html-react-parser handle them
      },
    };

    return (
      <Tabs variant="unstyled" defaultValue="thoughtProcess" className={className}>
        <Tabs.List className={classes.tabList}>
          <Tabs.Tab value="thoughtProcess" className={classes.tab}>Thought Process</Tabs.Tab>
          <Tabs.Tab value="sourceParagraphs" className={classes.tab}>Source Paragraphs</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="thoughtProcess">
          <Stack gap="md">
            <Text key='agent-classification' className={classes.agent_classification}>
              <strong>Agent Used: </strong>{agent_classification?.join(', ')}
            </Text>
            {thoughtContent.trim() && (
              <div key={'sanitized-thoughts'} className={classes.paragraph}>
                {parse(sanitizedThoughts, options)}
              </div>
            )}
            {retrievedDocuments && (
              <div className={classes.sourceDocumentsPanel}>
                <div className={classes.sourceDocumentsHeader}>
                  <div>
                    <Text size="sm" fw={600}>Source documents</Text>
                    <Text size="xs" c="dimmed">Retrieved chunks are hidden by default.</Text>
                  </div>
                  <Button
                    type="button"
                    size="xs"
                    variant={sourceDocumentsExpanded ? 'light' : 'subtle'}
                    leftSection={<IconFileText size={14} />}
                    onClick={() => setSourceDocumentsExpanded((expanded) => !expanded)}
                    aria-expanded={sourceDocumentsExpanded}
                  >
                    {sourceDocumentsExpanded ? 'Hide source documents' : 'Show source documents'}
                  </Button>
                </div>
                {sourceDocumentsExpanded && (
                  <Collapse in={sourceDocumentsExpanded}>
                    <div className={classes.sourceDocumentsContent}>
                      <MessageMarkdown
                        content={truncateSourceChunksInMarkdownForDisplay(retrievedDocuments.sourceContent)}
                        onCitationClick={() => {}}
                        onFollowUpClick={() => {}}
                        onCitationsExtracted={() => {}}
                        className={classes.paragraph}
                      />
                    </div>
                  </Collapse>
                )}
              </div>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="sourceParagraphs">
          <Stack gap="md">
            {renderDataPoints}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    );
  }, [content, agent_classification, data_points, className, isLoading, sourceDocumentsExpanded]);

  /* Reasoning not returned by backend -- if in figma review and maybe phase 2??
  const renderReasoning = () => {
    if (!reasoning || reasoning.length === 0) return null;

    return (
      <Box className={classes.reasoningSection}>
        <Text className={classes.reasoningTitle}>
          Key Reasoning Steps:
        </Text>
        <List spacing="xs" className={classes.reasoningList}>
          {reasoning.map((step, index) => (
            <List.Item key={index} className={classes.reasoningItem}>
              {step}
            </List.Item>
          ))}
        </List>
      </Box>
    );
  };
  */

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Box className={className}>
      <Stack gap="lg">
        {/* Main Content */}
        {renderMainContent()}
      </Stack>
    </Box>
  );
};

export default ThoughtProcessContent;
